/**
 * mountServerSideGrid
 *
 * Shared mount logic for AG Grid's server-side row model driven over a
 * LiveView socket, factored out of ParameterLogsAgGridHook — the first grid
 * built against a table (parameter_push_logs) that grows without bound.
 *
 * A page-specific hook supplies its column defs and two event names; this
 * covers what's identical everywhere: creating the grid, wiring the
 * getRows datasource to a pushEvent-with-reply, and purging the block
 * cache on a `refreshEvent` (fired after a filter change or a mutation,
 * since the grid's cached blocks were fetched under the old state).
 */
export function mountServerSideGrid(hook, { columnDefs, rowsEvent, refreshEvent, pageSize = 50, gridOptions = {} }) {
  if (!window.agGrid || !window.agGrid.createGrid) {
    console.error(`${rowsEvent}: agGrid not found on window`);
    return null;
  }

  const gridApi = window.agGrid.createGrid(hook.el, {
    columnDefs,
    rowModelType: "serverSide",
    cacheBlockSize: pageSize,
    // Two blocks in flight is enough to scroll/page smoothly without
    // letting a fast user queue a dozen queries against the database.
    maxConcurrentDatasourceRequests: 2,
    // Bounded so a long session can't grow the tab's memory without limit.
    maxBlocksInCache: 10,
    pagination: true,
    paginationPageSize: pageSize,
    domLayout: "normal",
    defaultColDef: {
      resizable: true,
      sortable: true,
      floatingFilter: false,
      filter: false,
    },
    animateRows: true,
    overlayNoRowsTemplate: `<span style="padding:20px;color:#6B7280;">${
      hook.el.dataset.empty || "No rows found."
    }</span>`,
    serverSideDatasource: {
      getRows: (params) => {
        hook.pushEvent(rowsEvent, { request: params.request }, (reply) => {
          if (reply && Array.isArray(reply.rows)) {
            params.success({ rowData: reply.rows, rowCount: reply.total });
          } else {
            params.fail();
          }
        });
      },
    },
    ...gridOptions,
  });

  hook.handleEvent(refreshEvent, () => {
    if (gridApi) gridApi.refreshServerSide({ purge: true });
  });

  return gridApi;
}
